import React, { useEffect, useState, useCallback, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SignatureField from "../../../components/SignatureField";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import Swal from "sweetalert2";

const AODASupervisionNotePreview = ({ data, onClose }) => {
  // State
  const [note, setNote] = useState({});
  const [formMeta, setFormMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Signature State
  const [patientSigLink, setPatientSigLink] = useState("");
  const [openClientSignatureModal, setOpenClientSignatureModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const sigCanvasRef = useRef(null);

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

  // Form ID extraction
  const { patientFormId, formTitle, practiceLogo, particeName } = data || {};

  // ========== Helpers (Dates & Utils) ==========
  
  const toDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    if (typeof val === "number") {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    const s = String(val).trim();

    // ISO: 2025-10-01 or 2025-10-01T06:21:32.433
    let m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (m) {
      const yyyy = +m[1],
        mm = +m[2] - 1,
        dd = +m[3];
      const hh = +(m[4] || 0),
        min = +(m[5] || 0),
        sec = +(m[6] || 0);
      return new Date(yyyy, mm, dd, hh, min, sec);
    }

    // US: MM/DD/YYYY [hh:mm AM/PM]
    m = s.match(
      /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/i
    );
    if (m) {
      let mm = +m[1],
        dd = +m[2],
        yyyy = +m[3];
      if (yyyy < 100) yyyy += 2000;
      let hh = +(m[4] || 0),
        min = +(m[5] || 0);
      const ap = (m[6] || "").toUpperCase();
      if (ap === "PM" && hh < 12) hh += 12;
      if (ap === "AM" && hh === 12) hh = 0;
      return new Date(yyyy, mm - 1, dd, hh, min, 0);
    }

    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = useCallback((val) => {
    const d = toDate(val);
    if (!d) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }, []);

  const fmtDateTime = useCallback((val) => {
    const d = toDate(val);
    if (!d) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    let h = d.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const hh = String(h).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
  }, []);

  const firstNonEmpty = (...vals) =>
    vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") || "";

  const parseBool = (v) => {
    if (typeof v === "boolean") return v;
    if (v === 1) return true;
    if (v === 0) return false;
    const s = String(v).trim().toLowerCase();
    return ["true", "yes", "y", "1"].includes(s);
  };

  // Safe JSON fetch
  async function fetchJSONSafe(url, options = {}) {
    const res = await fetch(url, {
      headers: { Accept: "application/json", ...(options.headers || {}) },
      ...options,
    });
    let data = {};
    if (res.status !== 204) {
      const text = await res.text();
      if (text && text.trim()) {
        try {
          data = JSON.parse(text);
        } catch {
          data = {};
        }
      }
    }
    return { ok: res.ok, status: res.status, data };
  }

  // Get Image Blob
  async function getClientImage(path, token) {
    try {
      if (!path) return "";
      if (/^(https?:|data:)/i.test(path)) return path;
      const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
        path
      )}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return "";
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (ct.includes("text/plain")) {
        const link = await res.text();
        return link || "";
      }
      const blob = await res.blob();
      return blob && blob.size ? URL.createObjectURL(blob) : "";
    } catch {
      return "";
    }
  }

  // ========== Signature Phrase Builder (Staff) ==========
  const fromEmail = (email) => {
    if (!email) return { name: "—", username: "" };
    const local = String(email).split("@")[0] || "";
    const formatted = local
      .replace(/[._-]/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
    return { name: formatted || "—", username: local || "" };
  };

  const mkPhraseFromEmailStrict = useCallback(
    (email, dt) => {
      const when = fmtDateTime(dt);
      if (!when) return ""; // No date => treat as no signature
      const { name, username } = fromEmail(email);
      const who = username ? `${name}, ${username}` : name || "—";
      return `Digitally signed by ${who} on ${when}.`;
    },
    [fmtDateTime]
  );

  // ========== Handlers for Client Signature ==========
  
  const openClientModal = () => setOpenClientSignatureModal(true);
  
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;
    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);
    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) swalEl.style.zIndex = "9999";
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      // Clear local state
      setPatientSigLink("");

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        const client = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        // Send API request to clear signature
        await client.post("/Forms/AddPatientForm", {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? patientFormId,
          patientFormId: patientFormId,
          clientsBase64: "",
          clientsURL: "",
        });

        Swal.fire({
          title: "Deleted!",
          text: "Signature has been removed.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
          },
        });
      } catch (e) {
        console.error(e);
        Swal.fire({
          title: "Error!",
          text: "Failed to remove signature.",
          icon: "error",
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
          },
        });
      }
    });
  };

  const handleSaveForm = async () => {
    if (!patientFormId) return;

    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const isBase64 = patientSigLink && patientSigLink.toString().includes("base64");

      const payload = {
        ...formMeta,
        Id: formMeta.Id ?? formMeta.id ?? patientFormId,
        patientFormId: patientFormId,
        clientsBase64: isBase64 ? patientSigLink : "",
        clientsURL: isBase64 ? "" : (patientSigLink || ""),
      };

      await client.post("/Forms/AddPatientForm", payload);

      setShowSuccessAlert(true);
    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
        },
      });
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    if (onClose) {
      onClose();
    } else {
      try {
        const backdrop = document.querySelector(".MuiBackdrop-root");
        if (backdrop) backdrop.click();
      } catch (e) { console.log(e) }
    }
  };

  // ========== Data Loading ==========
  useEffect(() => {
    let active = true;

    (async () => {
      setNote({});
      setFormMeta({});
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        if (!token) throw new Error("Authentication token not found.");
        if (!API_BASE_URL) throw new Error("API base URL is not configured.");
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch Supervision Note Data
        const [formRes, noteRes] = await Promise.all([
          fetchJSONSafe(
            `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`,
            { headers }
          ),
          fetchJSONSafe(
            `${API_BASE_URL}/Supervision/GetSupervisionNoteById?patientFormId=${patientFormId}`,
            { headers }
          ),
        ]);

        if (!active) return;

        const formRaw = formRes.ok ? formRes.data : {};
        const noteRaw = noteRes.ok ? noteRes.data : {};

        const formData = Array.isArray(formRaw) ? formRaw[0] || {} : formRaw || {};
        const noteData = Array.isArray(noteRaw) ? noteRaw[0] || {} : noteRaw || {};

        setFormMeta(formData);
        setNote(noteData);

        // Fetch Client Signature Image
        const clientPath = firstNonEmpty(
          noteData.clientsURL,
          noteData.ClientsURL,
          noteData.clientSignatureURL,
          noteData.patientSignatureURL,
          formData.clientsURL,
          formData.ClientsURL,
          formData.clientSignatureURL,
          formData.patientSignatureURL
        );
        const img = await getClientImage(clientPath, token);
        if (active && img) {
            setPatientSigLink(img);
        }
      } catch (e) {
        if (active) setError(e.message || "Failed to load data");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [patientFormId, API_BASE_URL]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // ========== Derived Values for Rendering ==========

  // Dates & Header info
  const sessionDate = fmtDate(
    firstNonEmpty(
      note.assessmentDate,
      formMeta.assessmentDate,
      note.sessionDate,
      formMeta.sessionDate,
      formMeta.SessionDate,
      formMeta.dateOfService,
      formMeta.DOS
    )
  );

  const titleText =
    firstNonEmpty(
      note.customFormTitle,
      formMeta.customFormTitle,
      note.title,
      formMeta.title,
      formMeta.formTitle,
      formMeta.Title,
      formTitle
    ) || "AODA Supervision/Collaboration Note";

  const clientName =
    firstNonEmpty(
      note.patientName,
      formMeta.patientName,
      note.clientName,
      formMeta.clientName,
      formMeta.ClientName
    ) || "—";

  const dobText = fmtDate(
    firstNonEmpty(note.dob, formMeta.dob, formMeta.DOB, formMeta.dateOfBirth)
  );

  const fileNoVal =
    firstNonEmpty(
      formMeta.fileNo,
      formMeta.FileNo,
      formMeta.fileNumber,
      formMeta.fileno,
      formMeta.accountNo
    ) || "—";

  // Checkbox Flags
  const printed = parseBool(firstNonEmpty(note.isPrinted, formMeta.isPrinted));
  const auditReady = parseBool(
    firstNonEmpty(note.isAuditReady, formMeta.isAuditReady)
  );

  const inactive = parseBool(
    firstNonEmpty(
      note.isChecked,
      formMeta.isChecked,
      note.inactive,
      formMeta.inactive,
      formMeta.Isinactive
    )
  );

  const locked = parseBool(
    firstNonEmpty(
      note.isLocked,
      formMeta.isLocked,
      note.formlock,
      formMeta.formlock,
      note.islocked,
      formMeta.islocked
    )
  );

  // Form Specific Checkboxes
  const clinicalSupervision = parseBool(
    firstNonEmpty(
      note.clinicalSupervision,
      formMeta.clinicalSupervision,
      formMeta.ClinicalSupervision
    )
  );
  const clinicalCollaboration = parseBool(
    firstNonEmpty(
      note.clinicalCollaboration,
      formMeta.clinicalCollaboration,
      formMeta.ClinicalCollaboration
    )
  );
  const isIndividual = parseBool(
    firstNonEmpty(
      note.individual,
      note.isIndividual,
      formMeta.individual,
      formMeta.isIndividual,
      formMeta.Individual
    )
  );
  const isGroup = parseBool(
    firstNonEmpty(
      note.groups,
      note.isGroup,
      formMeta.groups,
      formMeta.isGroup,
      formMeta.Group,
      formMeta.groupSelected,
      formMeta.isGroupSelected
    )
  );

  // Texts
  const staffText =
    firstNonEmpty(
      note.staff,
      formMeta.staff,
      note.staffName,
      formMeta.staffName,
      note.staffMember,
      formMeta.staffMember,
      note.clinicianName,
      formMeta.clinicianName
    ) || "—";

  const discussionText =
    firstNonEmpty(
      note.disscussion,
      note.discussion,
      note.discussionText,
      note.discussions,
      note.discussionObservationsRecommendations,
      note.DiscussionObservationsRecommendations,
      note.description,
      note.notes,
      note.remarks,
      formMeta.discussion,
      formMeta.description,
      formMeta.notes
    ) || "—";

  // Staff Signatures (Read Only Text)
  const internPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.internSignURL,
      formMeta.internSignURL,
      note.internSignatureURL,
      formMeta.internSignatureURL
    ),
    firstNonEmpty(
      note.staffsignatureDATE,
      formMeta.staffsignatureDATE,
      note.internSignatureDATE,
      formMeta.internSignatureDATE,
      note.internSignatureDate,
      formMeta.internSignatureDate,
      note.internSignatureDateTime
    )
  );

  const supervisedPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.supervisedClinicianURL,
      formMeta.supervisedClinicianURL,
      note.cliniciansignatureURL,
      note.clinicianSignatureURL,
      formMeta.clinicianSignatureURL
    ),
    firstNonEmpty(
      note.supervisedDate,
      formMeta.supervisedDate,
      note.cliniciansignatureDATE,
      note.clinicianSignatureDATE,
      formMeta.clinicianSignatureDATE
    )
  );

  const supervisorPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.supervisorSignURL,
      formMeta.supervisorSignURL,
      note.supervisorSignatureURL,
      formMeta.supervisorSignatureURL
    ),
    firstNonEmpty(
      note.supervisorSignatureDATE,
      formMeta.supervisorSignatureDATE
    )
  );

  const directorPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.treatmentdirectorURL,
      formMeta.treatmentdirectorURL,
      note.directorSignatureURL,
      formMeta.directorSignatureURL
    ),
    firstNonEmpty(
      note.directorDate,
      formMeta.directorDate,
      note.treatmentDirectorSignatureDATE,
      formMeta.treatmentDirectorSignatureDATE
    )
  );

  const clientSignedOn = fmtDate(
    firstNonEmpty(
      note.clientSignatureDATE,
      formMeta.clientSignatureDATE,
      note.clientSignatureDate,
      formMeta.clientSignatureDate
    )
  );

  const renderDigitalRow = (label, phraseText) => (
    <Grid container spacing={2} alignItems="baseline" sx={{ mb: 2 }}>
      <Grid item xs={12} sm={3}>
        <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={9}>
        {phraseText ? (
          <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "1rem" }, wordBreak: "break-word" }}>
            {phraseText}
          </Typography>
        ) : (
          <Typography color="text.secondary">—</Typography>
        )}
      </Grid>
    </Grid>
  );

  return (
    <>
      {/* Success Popup */}
      {showSuccessAlert && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              textAlign: "center",
              zIndex: 1300,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Form Saved
            </Typography>
            <Typography style={{ marginBottom: "20px" }}>
              Form saved successfully
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSuccessClose}
            >
              OK
            </Button>
          </div>
        </>
      )}

      <Paper
        sx={{
          p: 0,
          borderRadius: 2,
          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
          bgcolor: "#fff",
        }}
      >
        {/* Responsive Header */}
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: { xs: 2, sm: 3, md: 4 },
            pb: 0,
            mb: 3,
          }}
        >
          {isMdDown ? (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  mb: 1,
                }}
              >
                <strong>DOS:</strong> {sessionDate}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                  mb: 1,
                  textAlign: "center",
                }}
              >
                {titleText}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                <strong>DOS:</strong> {sessionDate}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {titleText}
              </Typography>
              <Box sx={{ height: 28 }} />
            </Box>
          )}

          {practiceLogo && (
            <Box
              component="img"
              src={practiceLogo}
              alt="Practice Logo"
              sx={{ width: 65, height: 65, borderRadius: 1, mb: 2, mx: "auto", display: "block" }}
            />
          )}
          {particeName && (
            <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
              {particeName}
            </Typography>
          )}

          <Divider sx={{ mt: isMdDown ? 0 : 0 }} />
        </Box>

        {/* Patient strip (File # | Client | DOB) */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                <strong>File #</strong> {fileNoVal}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "center" }, mt: { xs: 1, md: 0 } }}>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                <strong>Client:</strong> {clientName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" }, mt: { xs: 1, md: 0 } }}>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                <strong>DOB:</strong> {dobText}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 3 }} />
        </Box>

        {/* Title + Session Date in Grid */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 2,
            }}
          >
            <TextField
              size="small"
              label="Title"
              value={titleText}
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Session Date"
              value={sessionDate}
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Flags */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={printed} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Printed and Filed</Typography>}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={inactive} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Inactive</Typography>}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={auditReady} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Audit Ready</Typography>}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={locked} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Lock</Typography>}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3, mx: { xs: 2, sm: 3, md: 4 } }} />

        {/* Supervision / Case Consultation */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Supervision / Case Consultation
          </Typography>

          <Box sx={{ mb: 2, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 2 }}>
            <Typography variant="body2" sx={{ minWidth: { sm: 100 }, fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.875rem" } }}>
              Duration:
            </Typography>
            <TextField
              size="small"
              value={firstNonEmpty(note.duration, formMeta.duration, "—")}
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              sx={{ width: { xs: "100%", sm: 240 } }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={clinicalSupervision} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>Clinical Supervision</Typography>}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={clinicalCollaboration} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>Clinical Collaboration</Typography>}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={isIndividual} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>Individual</Typography>}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={isGroup} disabled size="small" />}
                label={<Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>Group</Typography>}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2, mx: { xs: 2, sm: 3, md: 4 } }} />

        {/* Staff */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Staff
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={staffText}
            InputProps={{ readOnly: true }}
          />
        </Box>

        <Divider sx={{ my: 2, mx: { xs: 2, sm: 3, md: 4 } }} />

        {/* Discussion */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Discussion, Observations, and Recommendations
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={discussionText}
            InputProps={{ readOnly: true }}
          />
        </Box>

        <Divider sx={{ my: 2, mx: { xs: 2, sm: 3, md: 4 } }} />

        {/* Signatures */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mt: 3, pb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Signatures
          </Typography>

          {/* Client Signature (Interactive) */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                Client Signature:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                {patientSigLink ? (
                  <>
                    <Box
                        component="img"
                        src={patientSigLink}
                        alt="Client Signature"
                        sx={{
                            width: 200,
                            height: 80,
                            border: "2px solid #000",
                            backgroundColor: "#f5f5f5",
                            p: 1,
                            borderRadius: 1,
                            objectFit: "contain",
                        }}
                    />
                    {clientSignedOn && clientSignedOn !== "—" && (
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                        Signed on {clientSignedOn}
                      </Typography>
                    )}
                    <Tooltip title="Edit Signature" arrow>
                        <Button
                            onClick={openClientModal}
                            variant="outlined"
                            size="small"
                            style={{ minWidth: "32px", padding: "4px" }}
                            aria-label="Edit Signature"
                        >
                            <EditIcon />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Remove Signature" arrow>
                        <Button
                            onClick={handleDeleteClientSignature}
                            style={{ minWidth: "32px", padding: "4px" }}
                            aria-label="Remove Signature"
                        >
                            <RemoveCircleOutlineIcon style={{ color: "red" }} />
                        </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Link component="button" onClick={openClientModal}>
                    Open signing window
                  </Link>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Read Only Staff Signatures */}
          {renderDigitalRow("Intern Signature:", internPhrase)}
          {renderDigitalRow("Supervised Clinician:", supervisedPhrase)}
          {renderDigitalRow("Supervisor Signature:", supervisorPhrase)}
          {renderDigitalRow("ARMHS Treatment Director:", directorPhrase)}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Signature Modal */}
      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Client Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
           <SignatureField ref={sigCanvasRef} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AODASupervisionNotePreview;